import React from 'react';
import PropTypes from 'prop-types';
import Checkbox from 'components/common/checkbox-component';
import { connect } from 'react-redux';
import ConditionalRender from 'components/util/conditional-render-component';
import Search from 'components/common/search-component';
import { SectionTreeList } from 'models/api/section-tree-list-model';
import { getRevisionsSectionsRequest } from 'api/sections-api';
import OCRNavigationModel from 'models/api/ocr-navigation-api-model';
import { injectIntl } from 'react-intl';
import { SectionCacheOCR } from 'models/api/sections-cache-model-ocr-data';

export const NOTES_STATEMENT_SEGMENT_FILTERS_ID_BLOCK =
  'notes-statement-segment-id';

export const NotesStatementSegmentFilter = ({
  className,
  revisionId,
  selectedSegments,
  updateSelectedSegmentsObject,
  isOCR,
  ocrCache,
  intl,
}) => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [sections, setSections] = React.useState(
    new SectionTreeList({ isLoading: true }),
  );
  const [sectionIdList, setSectionIdList] = React.useState([]);

  let validSearchTerm = '';
  if (searchTerm.length > 1) {
    validSearchTerm = searchTerm;
  }

  React.useEffect(() => {
    ocrCache.isLoaded && ocrCache?.data?.map
      ? setSectionIdList(
          Object.keys(ocrCache?.data?.map).map((key) => key.toString()),
        )
      : setSectionIdList([]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ocrCache.isLoaded]);

  React.useEffect(() => {
    if (!isOCR) {
      const fetchSectionList = async () => {
        try {
          const response = await getRevisionsSectionsRequest({
            revisionId: revisionId,
          });
          setSections(sections.setLoaded({ response }));
        } catch (error) {
          setSections(sections.setError(error));
        }
      };
      if (!sections.hasBeenFetched) {
        fetchSectionList();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOCR]);

  const getSectionsTranslation = (section) =>
    intl.formatMessage(
      {
        id: 'chatbot-response.page-number',
      },
      { pageNumber: section },
    );

  return (
    <div className={`${className}__statement-segment`}>
      <Search
        className={`${className}__statement-segment-search`}
        id={`${className}-search`}
        onChange={(val) => setSearchTerm(val)}
        onClear={() => {
          setSearchTerm('');
        }}
        placeholder={
          isOCR
            ? 'notes-panel.advanced-filter.ocr.statement-segment.search.placeholder'
            : 'notes-panel.advanced-filter.statement-segment.search.placeholder'
        }
        showSearchButton
        value={searchTerm}
        isValid
        disableAutoComplete
      />
      {isOCR ? (
        <ConditionalRender dependencies={[ocrCache]}>
          {sectionIdList
            .filter((sectionId) => sectionId.includes(searchTerm))
            .map((sectionId) => (
              <Checkbox
                key={sectionId}
                id={`${NOTES_STATEMENT_SEGMENT_FILTERS_ID_BLOCK}-${sectionId}`}
                isNotIntl
                label={getSectionsTranslation(sectionId)}
                value={sectionId}
                checked={Boolean(selectedSegments[sectionId])}
                onChange={(val) =>
                  updateSelectedSegmentsObject({
                    id: sectionId,
                    name: getSectionsTranslation(val),
                  })
                }
              />
            ))}
        </ConditionalRender>
      ) : (
        <ConditionalRender dependencies={[sections]}>
          {sections
            .filteredSectionsHierarchy(validSearchTerm)
            .map((section) => (
              <Checkbox
                key={section.id}
                id={`${NOTES_STATEMENT_SEGMENT_FILTERS_ID_BLOCK}-${section.id}`}
                isNotIntl
                label={section.name}
                value={section}
                checked={Boolean(selectedSegments[section.id])}
                onChange={(section) => updateSelectedSegmentsObject(section)}
              />
            ))}
        </ConditionalRender>
      )}
    </div>
  );
};

const mapStateToProps = ({
  data: {
    revision,
    statementContent: {
      sectionsCache: {
        contentSectionMap: { data: map },
        ocrCache,
      },
    },
  },
}) => {
  return {
    contentSectionMap: map,
    revisionId: revision.id,
    ocrCache,
  };
};

NotesStatementSegmentFilter.propTypes = {
  /** Optional classname property applied to parent div */
  className: PropTypes.string,
  // id of current revision
  revisionId: PropTypes.number.isRequired,
  /** object of all the selected segments */
  selectedSegments: PropTypes.object.isRequired,
  /** method to updated the object of selected segments */
  updateSelectedSegmentsObject: PropTypes.func.isRequired,
  /** is OCR statement */
  isOCR: PropTypes.bool,
  /** OCR Navigation data */
  statementOCRNavigation: PropTypes.instanceOf(OCRNavigationModel),
  /** intl object from react-intl */
  intl: PropTypes.object.isRequired,
  /** OCR Section Cache */
  ocrCache: PropTypes.instanceOf(SectionCacheOCR),
};

export default connect(mapStateToProps)(
  injectIntl(NotesStatementSegmentFilter),
);
